import type { Region } from "./regions";
import numbers from "./short-numbers.json";

export interface Short {
  // The region it is short within, which is the picked one and never a region the digits named: three digits name
  // nothing on their own, and the same three are an emergency service here and a carrier's own line there.
  region: Region;
  digits: string;
  emergency: boolean;
  cost: string;
  carrierSpecific: boolean;
  smsService: boolean;
}

// What `bun run phone-geo` reads out of Google's ShortNumberMetadata.xml: a range written as a regular expression,
// per region and per kind. It is 43 kB and rides this chunk rather than being fetched like the maps, which is what
// keeps a short code answered on the keystroke that finishes it.
const SHORT: Record<string, Patterns | undefined> = numbers;

interface Patterns {
  shortCode: string;
  emergency?: string;
  tollFree?: string;
  standardRate?: string;
  premiumRate?: string;
  carrierSpecific?: string;
  smsServices?: string;
}

export function shortReading(digits: string, region: Region): Short | undefined {
  const patterns = SHORT[region.code];
  if (!patterns || !matches(patterns.shortCode, digits)) return undefined;

  const emergency = matches(patterns.emergency, digits);
  return {
    region,
    digits,
    emergency,
    cost: cost(patterns, digits, emergency),
    carrierSpecific: matches(patterns.carrierSpecific, digits),
    smsService: matches(patterns.smsServices, digits),
  };
}

// Google's own order, and it is an order rather than a set because the ranges overlap: the dearer answer is the one
// worth giving where two of them match. An emergency number is toll free by being one and is asked last, since a
// country that has said what its own emergency line costs has said something this need not overrule.
function cost(patterns: Patterns, digits: string, emergency: boolean): string {
  if (matches(patterns.premiumRate, digits)) return "Premium rate";
  if (matches(patterns.standardRate, digits)) return "Standard rate";
  if (matches(patterns.tollFree, digits) || emergency) return "Toll free";
  return "Unknown cost";
}

// Anchored at both ends, a range being what a whole number is in and not what it starts with, and kept once it is
// built: seven of these are asked about on every keystroke.
function matches(pattern: string | undefined, digits: string): boolean {
  if (!pattern) return false;
  let expression = compiled.get(pattern);
  if (!expression) compiled.set(pattern, expression = new RegExp(`^(?:${pattern})$`));
  return expression.test(digits);
}

const compiled = new Map<string, RegExp>();
